// React Imports
import React, { useEffect, useState } from "react";
// Material UI Imports
import {
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Box,
  Checkbox,
  Tooltip,
  Icon
} from "@material-ui/core";
import { CloudDownload, RotateLeft } from "@material-ui/icons";
import EditIcon from '@material-ui/icons/Edit';
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import SaveIcon from "@material-ui/icons/Save";
import { makeStyles } from "@material-ui/core/styles";
import sweetAlertStyle from "assets/jss/material-dashboard-pro-react/views/sweetAlertStyle.js";
import SweetAlert from "react-bootstrap-sweetalert";

// Styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";

// Custom component imports
import Button from "components/CustomButtons/Button.js";

// Custom plugins and utils
import NotificationContext from "views/Components/Context.js";
import { formatISO } from "date-fns";
import Alert from "views/Components/Alert";

// API Imports
import {
  generateNotebookSpecificationsAPI,
  generateBusinessUseCaseAPI,
  saveTechnicalSpecificationsAPI,
  saveNotebookSpecificationsAPI,
  saveBusinessUseCaseAPI,
  saveIdeaAPI,
  regenerateFeedbackAPI,
  fetchLabAPI,
  convertToPDFForLabAPI,
  submitLabForNotebookGenerationAPI,
  saveStreamlitSpecificationsAPI,
  generateStreamlitSpecificationsAPI,
  submitLabForGenerationAPI
} from "views/APIs/APIUtility";
import useMetaPrompt from "hooks/useMetaPrompt";
import QuStreamlineDialog from "views/Components/QuStreamlineDialog";
import EditorViewer from "views/Components/EditorViewer";

import SubmitLabModal from "views/Components/SubmitLabModal";

const identifiers = {
  research_report: "Research Report",
  white_paper: "White Paper",
  project_plan: "Project Plan",
  blog: "Blog",
  news_letter: "Newsletter",
  case_study: "Case Study",
  key_insights: "Key Insights",
  handout: "Handout",
  idea: "Idea",
  business: "Business Use Case",
  technical: "QuResearch Specification",
  streamlit: "Streamlit Specifications",
  notebook: "Notebook Specification",
};

const usesweetAlertStyle = makeStyles(sweetAlertStyle);
const useStyles = makeStyles((theme) => ({
  ...styles,
}));

export default function LabUseCase({
  projectID,
  projectType,
  setActiveStep,
  useCase,
}) {

  const {
    metapromptCheckbox,
    setMetapromptCheckbox,
    originalMetaPrompt,
    setOriginalMetaPrompt,
    metaPrompt,
    setMetaPrompt,
    metaPromptDialogOpen,
    setMetaPromptDialogOpen,
    requiredMetaPromptParameters,
    setRequiredMetaPromptParameters,
    displayMetaPromptParameters,
    setDisplayMetaPromptParameters,
    notRequiredParameters,
    isDisabled,
    setIsDisabled,
    handleMetaPromptChange,
    checkForDisabled,
    _extractParameters,
    _handleEnhancePrompt,
  } = useMetaPrompt();

  const classes = useStyles();

  const category = localStorage.getItem("userCategory");
  const { showNotification } = React.useContext(NotificationContext);

  const sweetAlertClasses = usesweetAlertStyle();
  const [alert, setAlert] = React.useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [loadingState, setLoadingState] = useState({ isLoading: false, message: "" });
  const [modelDetails, setModelDetails] = useState({ company: "", model: "", apiKey: "", name: "", description: "" });

  // Mappings for API selection and PDF response keys
  const apiMap = {
    idea: saveIdeaAPI,
    business: saveBusinessUseCaseAPI,
    technical: saveTechnicalSpecificationsAPI,
    streamlit: saveStreamlitSpecificationsAPI,
    notebook: saveNotebookSpecificationsAPI,
  };

  const pdfMap = {
    idea: "idea_pdf",
    business: "business_use_case_pdf",
    technical: "technical_specifications_pdf",
    streamlit: "streamlit_specifications_pdf",
    notebook: "notebook_specifications_pdf",
  };

  // Define messages for different use cases
  const loadingMessages = {
    business: "Loading Business Use Case...",
    technical: "Loading Notebook Specifications...",
    idea: "Loading Idea Details...",
    feedback: "Regenerating content with feedback...",
    version_change: "Loading version content...",
    generate_from_idea: "Generating Business Use Case...",
    streamlit: "Loading Streamlit Specifications...",
    notebook: "Loading Notebook Specifications...",
  };
  const [feedback, setFeedback] = useState("");

  const [outline, setOutline] = useState("# Hello This is sample markdown");
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfLoading, setPdfLoading] = useState({ isLoading: false, message: "" });
  const [version, setVersion] = useState(1);
  const [latestVersion, setLatestVersion] = useState(1);
  const [versionOptions, setVersionOptions] = useState([
    {
      business_use_case: "",
      version: 1,
      timestamp: formatISO(new Date()),
    },
  ]);

  const [lab, setLab] = useState(null);
  const [license, setLicense] = useState("QuantUniversity License");
  const [framework, setFramework] = useState("robust");

  useEffect(() => {
    // Guard against updates after unmount
    let isCancelled = false;

    const fetchLabData = async () => {

      if (!projectID) return;

      setLoadingState({ isLoading: true, message: loadingMessages[useCase] ?? "Loading…" });
      setPdfLoading({ isLoading: true, message: "Loading PDF…" });

      const data = await fetchLabAPI(projectID);
      if (isCancelled) return;                       // stop if the component unmounted

      setLab(data);

      /** Map each use-case to the relevant fields in the response */
      const CASE_MAP = {
        idea: {
          outline: data?.idea,
          pdf: data?.idea_pdf,
          history: data?.idea_history,
        },
        business: {
          outline: data?.business_use_case,
          pdf: data?.business_use_case_pdf,
          history: data?.business_use_case_history,
        },
        technical: {
          outline: data?.technical_specifications,
          pdf: data?.technical_specifications_pdf,
          history: data?.technical_specifications_history,
        },
        notebook: {
          outline: data?.notebook_specifications,
          pdf: data?.notebook_specifications_pdf,
          history: data?.notebook_specifications_history,
        },
        streamlit: {
          outline: data?.streamlit_specifications,
          pdf: data?.streamlit_specifications_pdf,
          history: data?.streamlit_specifications_history,
        },
      };

      const current = CASE_MAP[useCase];

      // Always fall back to safe defaults
      setOutline(current.outline ?? "");
      setPdfUrl(current.pdf ?? "");

      const historyArr = current.history ?? [];
      setVersionOptions(historyArr);

      const latest = historyArr.length
        ? historyArr[historyArr.length - 1]
        : { version: 1 };

      setVersion(latest.version);
      setLatestVersion(latest.version);

      setLoadingState({ isLoading: false, message: "" });
      setPdfLoading({ isLoading: false, message: "" });
    };

    fetchLabData();
  }, [projectID, useCase]);

  const handleSaveChanges = async (reloadWindow = true) => {
    setLoadingState({ isLoading: true, message: "Saving changes..." });

    try {
      const formData = new FormData();
      formData.append("lab_id", projectID);

      // Validate useCase before proceeding
      if (!apiMap[useCase] || !pdfMap[useCase]) {
        throw new Error("Invalid use case selected");
      }

      let fieldKey;
      if (useCase === "idea") {
        fieldKey = "idea";
      } else if (useCase === "business") {
        fieldKey = "business_use_case";
      } else if (useCase === "technical") {
        fieldKey = "technical_specifications";
      } else if (useCase === "streamlit") {
        fieldKey = "streamlit_specifications";
      } else if (useCase === "notebook") {
        fieldKey = "notebook_specifications";
      } else {
        throw new Error("Invalid use case selected");
      }

      formData.append(fieldKey, outline);

      // Call the appropriate API function
      const data = await apiMap[useCase](formData);

      // Extract the correct PDF URL
      setPdfUrl(data[pdfMap[useCase]]);

      hideAlert();
      showNotification("Changes saved successfully", "success");

      setTimeout(() => {
        if (reloadWindow) {
          window.location.reload();
        }
      }, 1000);
    } catch (error) {
      showNotification(error.message || "An unexpected error occurred", "danger");
    } finally {
      setLoadingState({ isLoading: false, message: "" });
    }
  };


  const saveChangesAlert = () => {
    setAlert(
      <SweetAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title="Are you sure?"
        onConfirm={() => handleSaveChanges()}
        onCancel={() => hideAlert()}
        confirmBtnCssClass={
          sweetAlertClasses.button + " " + sweetAlertClasses.success
        }
        cancelBtnCssClass={
          sweetAlertClasses.button + " " + sweetAlertClasses.danger
        }
        confirmBtnText="Yes, Save Changes!"
        cancelBtnText="Cancel"
        showCancel
      >
        The changes will be saved as the latest version. Are you sure you want
        to save these changes?
      </SweetAlert>
    );
  };

  const hideAlert = () => {
    setAlert(null);
  };

  const getPdf = async (markdown, download) => {
    if (download) {
      showNotification("Generating PDF...", "warning");
    }
    const formData = new FormData();
    formData.append("lab_id", projectID);
    formData.append("markdown", markdown);
    if (useCase === "idea") {
      formData.append("template_name", "idea");
      formData.append("lab_design_step", 1);
    }
    else if (useCase === "business") {
      formData.append("template_name", "business");
      formData.append("lab_design_step", 2);
    } else if (useCase === "technical") {
      formData.append("template_name", "technical");
      formData.append("lab_design_step", 3);
    } else if (useCase === "notebook") {
      formData.append("template_name", "technical");
      formData.append("lab_design_step", 3);
    } else if (useCase === "streamlit") {
      formData.append("template_name", "technical");
      formData.append("lab_design_step", 4);
    }

    try {
      const data = await convertToPDFForLabAPI(formData);
      const url = data;
      setPdfUrl(url);

      if (download) {
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        if (useCase === "idea") {
          link.setAttribute("download", "Idea.pdf");
        }
        else if (useCase === "business") {
          link.setAttribute("download", "Business_Use_Case.pdf");
        } else if (useCase === "technical") {
          link.setAttribute("download", "Technical_Specifications.pdf");
        } else if (useCase === "notebook") {
          link.setAttribute("download", "Notebook_Specifications.pdf");
        } else if (useCase === "streamlit") {
          link.setAttribute("download", "Streamlit_Specifications.pdf");
        }
        document.body.appendChild(link);
        link.click();
        link.remove();
        showNotification("PDF generated successfully", "success");
      }
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  const regenerateWithFeedback = async () => {
    if (feedback.trim() === "") {
      showNotification("Please enter feedback", "danger");
      return;
    }
    try {
      setLoadingState({ isLoading: true, message: loadingMessages["feedback"] });
      const formData = new FormData();
      formData.append("content", outline);
      if (metapromptCheckbox) {
        formData.append("use_metaprompt", true);
      }
      formData.append("feedback", feedback);

      const response = await regenerateFeedbackAPI(formData);
      setOutline(response);
      await handleRefreshContent(response, false);

      showNotification("Content regenerated successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
      setLoadingState({ isLoading: false, message: "" });
    }
  };

  

  const LabUseCaseSubmitHandler = async () => {
    try {
      const formData = new FormData();
      formData.append("lab_id", projectID);
      if (useCase === "idea") {
        setLoadingState({ isLoading: true, message: loadingMessages["generate_from_" + useCase] });
        const prompt = metapromptCheckbox ? metaPrompt : originalMetaPrompt;
        formData.append("prompt", prompt);
        formData.append("use_metaprompt", !metapromptCheckbox);
        await generateBusinessUseCaseAPI(formData);
        setActiveStep(2);
      } else if (useCase === "business") {
        setLoadingState({ isLoading: true, message: loadingMessages["generate_from_" + useCase] });
        const prompt = metapromptCheckbox ? metaPrompt : originalMetaPrompt;
        formData.append("prompt", prompt);
        formData.append("use_metaprompt", !metapromptCheckbox);
        await generateNotebookSpecificationsAPI(formData);
        setActiveStep(3); // Move to next step only after successful API call
      }
      else if (useCase === "notebook") {
        setLoadingState({ isLoading: true, message: loadingMessages["generate_from_" + useCase] });
        formData.append("notebook_specifications", outline);
        await saveNotebookSpecificationsAPI(formData);
        formData.delete("notebook_specifications");
        formData.append("license", license);
        formData.append("framework", framework);
        await submitLabForNotebookGenerationAPI(formData);
        window.location.reload();
        setActiveStep(4); // Move to next step only after successful API call
      }
      else if (useCase === "technical") {
        setLoadingState({ isLoading: true, message: loadingMessages["generate_from_" + useCase] });
        await saveTechnicalSpecificationsAPI(formData);
        formData.delete("technical_specifications");
        formData.append("license", license);
        await submitLabForGenerationAPI(formData);
        window.location.reload();
        setActiveStep(5); // Move to next step only after successful API call
      }
      else if (useCase === "streamlit") {
        setLoadingState({ isLoading: true, message: loadingMessages["generate_from_" + useCase] });
        formData.append("streamlit_specifications", outline);
        await saveStreamlitSpecificationsAPI(formData);
        formData.delete("streamlit_specifications");
        formData.append("license", license);
        formData.append("framework", framework);
        await submitLabForGenerationAPI(formData);
        window.location.reload();
        setActiveStep(6); // Move to next step only after successful API call
      }
    } catch (error) {
      showNotification(error.message || "An error occurred", "danger");
    } finally {
      setMetapromptCheckbox(false);
      setLoadingState({ isLoading: false, message: "" });
    }
  };

  const convertUtcToLocalWithOffset = (utcTimeString, offsetHours = -5) => {
    const date = new Date(utcTimeString); // Convert UTC string to Date object

    // Add the offset hours
    date.setHours(date.getHours() + offsetHours);

    // Format to the user's local timezone
    return date.toLocaleString();
  };

  const useCaseStatusMap = {
    "In Design Phase": 0,
    "Lab Design": 1,
    "Idea Selection": 2,
    "Technical Specifications": 3,
    "QuResearch Specifications": 3,
    "notebook": 3,
    "In Notebook Generation Queue": 3,
    "Notebook Generation Failed": 3,
    "Notebook Specifications": 3,
    "Notebook Specifications Review": 3,
    "Notebook Review": 4,
    "In Lab Generation Queue": 5,
    "Lab Generation Failed": 5,
    "Lab Specifications": 5,
    "streamlit": 5,
    "Streamlit Specifications Review": 5,
    "Streamlit Specifications": 5,
    "Review": 6,
    "Published": 6,
    default: 0,
  }

  const submitHandler = async () => {
    const useCaseIndex = useCaseStatusMap[useCase];
    const statusIndex = useCaseStatusMap[lab.status];
    if (statusIndex > useCaseIndex) {
      setShowAlert(true);
    }
    else {
      handleSaveChanges(false);
      setReviewDialog(true);
    }
  };

  const handleChangeVersion = async (event) => {
    setLoadingState({ isLoading: true, message: loadingMessages["version_change"] });
    const selectedVersion = event.target.value;
    setVersion(selectedVersion);


    // Optionally find and use the selected version's details
    const selectedOption = versionOptions.find(
      (option) => option.version === selectedVersion
    );
    if (selectedOption) {

      if (useCase === "idea") {
        setOutline(selectedOption.idea);
        await getPdf(selectedOption.idea);
      }
      else if (useCase === "business") {
        setOutline(selectedOption.business_use_case);
        await getPdf(selectedOption.business_use_case);
      }
      else if (useCase === "streamlit") {
        setOutline(selectedOption.streamlit_specifications);
        await getPdf(selectedOption.streamlit_specifications);
      }
      else if (useCase === "notebook") {
        setOutline(selectedOption.notebook_specifications);
        await getPdf(selectedOption.notebook_specifications);
      }
      else {
        setOutline(selectedOption.technical_specifications);
        await getPdf(selectedOption.technical_specifications);
      }
    }
    setLoadingState({ isLoading: false, message: "" });
  };


  const handleRefreshContent = async (outline, download) => {
    setPdfLoading({ isLoading: true, message: "Loading PDF..." });
    await getPdf(outline, download);
    setPdfLoading({ isLoading: false, message: "" });
  };

  const handleMetaPromptDialog = async (event) => {
    setMetaPromptDialogOpen(true);
  };

  const metaPromptDialogClose = () => {
    setMetaPromptDialogOpen(false);
    setMetapromptCheckbox(false);
  };

  const _handleSubmission = async () => {
    setMetapromptCheckbox(true);
    setMetaPromptDialogOpen(false);
  };

  const handleConfirm = () => {
    setShowAlert(false); // Close the alert
    setReviewDialog(true);
  };

  const handleCancel = () => {
    setShowAlert(false); // Close the alert
  };

  if (loadingState.isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress className={classes.loaderClass} />
        <Typography variant="h6" style={{ marginLeft: 16, textTransform: 'none' }}>
          {loadingState.message || "Loading..."}
        </Typography>
      </Box>
    );
  }

  const handleReviewDialogClose = () => {
    setModelDetails({ company: "", model: "", apiKey: "", name: "", description: "" });
    setReviewDialog(false);
  };

  return (
    <div>
      <SubmitLabModal
        reviewDialog={reviewDialog}
        handleReviewDialogClose={handleReviewDialogClose}
        lab={lab}
        specifications={outline}
        modelDetails={modelDetails}
        setModelDetails={setModelDetails}
        submitHandler={LabUseCaseSubmitHandler}
        license={license}
        setLicense={setLicense}
        framework={framework}
        setFramework={setFramework}
      />
      {showAlert && (
        <Alert
          title="Are you sure?"
          warning={true}
          showCancel={true}
          cancelBtnText="No"
          confirmBtnText="Yes"
          onCancel={handleCancel}
          onConfirm={handleConfirm}
        >
          Proceeding to the next step will reset your current progress in further steps. Your existing work will be overwritten, and new content will be created. Are you sure you want to continue?
        </Alert>
      )}

      <QuStreamlineDialog
        metaPromptDialogOpen={metaPromptDialogOpen}
        metaPromptDialogClose={metaPromptDialogClose}
        metaPrompt={metaPrompt}
        setMetaPrompt={setMetaPrompt}
        displayMetaPromptParameters={displayMetaPromptParameters}
        notRequiredParameters={notRequiredParameters}
        handleMetaPromptChange={handleMetaPromptChange}
        handleSubmission={_handleSubmission}
        checkForDisabled={checkForDisabled}
        _handleEnhancePrompt={_handleEnhancePrompt}
        _handleSubmission={_handleSubmission}
        isDisabled={isDisabled}
        setIsDisabled={setIsDisabled}
      />

      {alert}
      <div
        style={{
          marginTop: "10px",
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "10px",
        }}
      >
        <div>
          <Typography variant="h5"> {identifiers[useCase]}</Typography>
          <Typography variant="body1">
            {" "}
            {useCase === "business"
              ? "Describe the business use case for the project."
              : useCase === "technical"
                ? "Describe the QuResearch specifications for the project."
                : useCase === "streamlit"
                  ? "Describe the Streamlit specifications for the project."
                  : useCase === "notebook"
                    ? "Describe the Notebook specifications for the project."
                    : "Describe the idea for the project."
            }
          </Typography>
        </div>
        {outline && (
          <div>
            <Button
              onClick={() => getPdf(outline, true)}
              color="quCreateColor"
              style={{
                float: "right",
              }}
            >
              <CloudDownload /> &nbsp;Download
            </Button>

            <Button
              onClick={() => {
                saveChangesAlert();
              }}
              color="quCreateColor"
              style={{
                float: "right",
              }}
              disabled={version !== latestVersion}
            >
              <Icon>save</Icon> &nbsp;Save New Version
            </Button>
            <FormControl
              style={{ minWidth: "180px", float: "right", marginRight: "10px" }}
            >
              {" "}
              <InputLabel>Select Version</InputLabel>
              <Select
                value={version}
                renderValue={(selected) => {
                  // Display only the version number when the dropdown is not open
                  const selectedOption = versionOptions.find(
                    (option) => option.version === selected
                  );
                  return selectedOption
                    ? `Version ${selectedOption.version}`
                    : "";
                }}
                onChange={handleChangeVersion}
                fullWidth
                disableUnderline
              >
                {versionOptions.map((option, index) => (
                  <MenuItem key={index} value={option.version}>
                    <Grid container>
                      <Grid item xs={12}>
                        <Typography variant="body1">
                          {" "}
                          {"Version: " + option.version}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          style={{ fontStyle: "italic" }}
                        >
                          {"Last Updated: " +
                            convertUtcToLocalWithOffset(option.timestamp)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}
      </div>
      <Divider style={{ marginTop: "10px" }} />
      {!outline && <><Typography variant="body1" style={{ marginTop: "10px" }}>No specifications available</Typography><Divider style={{ marginTop: "10px" }} /></>}
      

      {outline && <EditorViewer
        version={version}
        latestVersion={latestVersion}
        outline={outline}
        setOutline={setOutline}
        pdfUrl={pdfUrl}
        pdfLoading={pdfLoading}
        setPdfLoading={setPdfLoading}
        handleRefreshContent={handleRefreshContent}
      />}

      {outline && <>
        <Grid item xs={12} sm={12}>
          <Typography variant="h5" style={{ marginTop: "10px" }}>
            Feedback
          </Typography>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Typography
            variant="body1"
            style={{ marginTop: "10px", marginBottom: "10px" }}
          >
            Regenerate the output with your suggestions.
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          style={{ marginTop: "10px", marginBottom: "10px" }}
        >
          <TextField
            multiline
            fullWidth
            rows={6}
            placeholder="Enter your feedback here..."
            style={{
              backgroundColor: "white",
              padding: "10px",
            }}
            onChange={(e) => setFeedback(e.target.value)}
          />
        </Grid>
      </>}


      {outline && <Grid container style={{ marginTop: "10px" }}>


        <Grid item xs={12} sm={12}>

          <Button
            color="success"
            startIcon={<RotateLeft />}
            onClick={regenerateWithFeedback}
            style={{ float: "left" }}
          >
            Regenerate
          </Button>



          <Button
            color="quCreateColor"
            onClick={submitHandler}
            endIcon={<NavigateNextIcon />}
            style={{ float: "right", marginLeft: "10px" }}
          >
            Submit
          </Button>

          <Button
            onClick={() => {
              saveChangesAlert();
            }}
            color="quCreateColor"
            style={{
              float: "right",
            }}
            disabled={version !== latestVersion}
          >
            <SaveIcon /> &nbsp;Save Changes
          </Button>


          {/* {(category === "super_admin" || category === "admin") && (
            (useCase === "idea" || useCase === "business") && (
              <div
                style={{
                  float: "right",
                  display: "flex",
                  alignItems: "center",
                  verticalAlign: "middle",
                  marginTop: "5px"
                }}
              >
                <Checkbox color="quCreateColor" checked={metapromptCheckbox} disabled />
                <Typography variant="body2" color="textSecondary">
                  Enable QuStreamline
                </Typography>
                <Tooltip title="QuStreamline streamlines the generation of the query with best practices and guardrails.">
                  <EditIcon
                    style={{ marginLeft: "5px", cursor: "pointer" }}
                    fontSize="small"
                    color="quCreateColor"
                    onClick={handleMetaPromptDialog}
                  />
                </Tooltip>
              </div>
            )
          )} */}
        </Grid>
      </Grid>}
    </div >
  );
}
